import "@/App.css";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { FacilityProvider } from "@/context/FacilityContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AppShell } from "@/components/layout/AppShell";

import Login from "@/pages/auth/Login";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";

import PlatformDashboard from "@/pages/platform/PlatformDashboard";
import Organisations from "@/pages/platform/Organisations";
import PlatformFacilities from "@/pages/platform/PlatformFacilities";
import PlatformModules from "@/pages/platform/PlatformModules";
import PlatformUsers from "@/pages/platform/PlatformUsers";
import Subscriptions from "@/pages/platform/Subscriptions";
import PlatformAudit from "@/pages/platform/PlatformAudit";
import PlatformSettings from "@/pages/platform/PlatformSettings";

import TenantDashboard from "@/pages/tenant/TenantDashboard";
import FacilityInfo from "@/pages/tenant/FacilityInfo";
import Departments from "@/pages/tenant/Departments";
import Services from "@/pages/tenant/Services";
import ChargeManagement from "@/pages/tenant/ChargeManagement";
import Finance from "@/pages/tenant/Finance";
import TenantUsers from "@/pages/tenant/TenantUsers";
import Roles from "@/pages/tenant/Roles";
import EnabledModules from "@/pages/tenant/EnabledModules";
import TenantSettings from "@/pages/tenant/TenantSettings";
import TenantAudit from "@/pages/tenant/TenantAudit";

import OpdQueue from "@/pages/opd/OpdQueue";
import OpdPatients from "@/pages/opd/Patients";
import Consultation from "@/pages/opd/Consultation";
import OpdBilling from "@/pages/opd/OpdBilling";
import FollowUps from "@/pages/opd/FollowUps";
import ConnectionRequests from "@/pages/opd/ConnectionRequests";
import Appointments from "@/pages/opd/Appointments";
import IpdAdmissions from "@/pages/ipd/Admissions";
import IpdAdmissionDetail from "@/pages/ipd/AdmissionDetail";
import IpdAdmit from "@/pages/ipd/Admit";
import IpdBeds from "@/pages/ipd/Beds";
import PortalApp from "@/pages/portal/PortalApp";
import HealthApp from "@/pages/health/HealthApp";

function RootRedirect() {
  const { auth } = useAuth();
  if (auth === null) return null;
  if (auth === false) return <Navigate to="/login" replace />;
  return <Navigate to={auth.is_platform_admin ? "/platform" : "/app"} replace />;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />

            <Route path="/platform" element={<ProtectedRoute platformOnly><FacilityProvider><AppShell /></FacilityProvider></ProtectedRoute>}>
              <Route index element={<PlatformDashboard />} />
              <Route path="organisations" element={<Organisations />} />
              <Route path="facilities" element={<PlatformFacilities />} />
              <Route path="modules" element={<PlatformModules />} />
              <Route path="users" element={<PlatformUsers />} />
              <Route path="subscriptions" element={<Subscriptions />} />
              <Route path="audit" element={<PlatformAudit />} />
              <Route path="settings" element={<PlatformSettings />} />
            </Route>

            <Route path="/app" element={<ProtectedRoute tenantOnly><FacilityProvider><AppShell /></FacilityProvider></ProtectedRoute>}>
              <Route index element={<TenantDashboard />} />
              <Route path="opd" element={<OpdQueue />} />
              <Route path="opd/appointments" element={<Appointments />} />
              <Route path="opd/patients" element={<OpdPatients />} />
              <Route path="opd/consultation/:visitId" element={<Consultation />} />
              <Route path="opd/billing" element={<OpdBilling />} />
              <Route path="opd/followups" element={<FollowUps />} />
              <Route path="opd/connections" element={<ConnectionRequests />} />
              <Route path="ipd/admissions" element={<IpdAdmissions />} />
              <Route path="ipd/admissions/:admissionId" element={<IpdAdmissionDetail />} />
              <Route path="ipd/admit" element={<IpdAdmit />} />
              <Route path="ipd/beds" element={<IpdBeds />} />
              <Route path="facility" element={<FacilityInfo />} />
              <Route path="departments" element={<Departments />} />
              <Route path="services" element={<Services />} />
              <Route path="charges" element={<ChargeManagement />} />
              <Route path="finance" element={<Finance />} />
              <Route path="users" element={<TenantUsers />} />
              <Route path="roles" element={<Roles />} />
              <Route path="modules" element={<EnabledModules />} />
              <Route path="audit" element={<TenantAudit />} />
              <Route path="settings" element={<TenantSettings />} />
            </Route>

            <Route path="/portal/*" element={<PortalApp />} />
            <Route path="/health/*" element={<HealthApp />} />

            <Route path="/" element={<RootRedirect />} />
            <Route path="*" element={<RootRedirect />} />
          </Routes>
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
